import { FC, Fragment, useEffect, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { IEvent } from "../../../models/event";
import { EVENT_TEST_TYPES, EVENT_TYPES } from "../../../enums/event";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { IEventResult } from "../../../models/eventResult";
import GenericModalV2 from "../../../shared/GenericModal/GenericModal";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "./CompileEvent.module.scss";
import { CompileCmjEvent } from "./CompileCmjEvent";
import { CompileRpeEvent } from "./CompileRpeEvent";
import { CompileDropJumpEvent } from "./CompileDropJumpEvent";
import { CompileSystemTestEvent } from "./CompileSystemTestEvent";

const LoadEventProvider: FC<{
  event: IEvent;
  onClose: () => void;
}> = ({ event, onClose }) => {
  const enpoints = useEndpoints();
  const [data, setData] = useState<IEvent>();
  const [eventResults, setEventResults] = useState<IEventResult[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchEvent = async () => {
    setIsLoading(true);
    try {
      const { data: response } = await enpoints.getEvent(event._id);
      const { data: response2 } = await enpoints.getEventResults({
        eventId: event._id,
        limit: 500,
      });
      setEventResults(response2.docs);
      setData(response);
    } catch (error) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="w-100 flex-1 d-flex justify-content-center align-items-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={style.errorState}>
        <WarningCircleIcon weight="fill" />
        <Typhography size="bold-14">Errore nel caricamento</Typhography>
        <Typhography size="regular-12">
          Non è stato possibile caricare i dati dell'evento.
        </Typhography>
      </div>
    );
  }

  if (data) {
    return (
      <>
        {eventResults &&
          data.type === EVENT_TYPES.TEST &&
          data.testType === EVENT_TEST_TYPES.CMJ && (
            <CompileCmjEvent
              event={data}
              eventResults={eventResults}
              onClose={onClose}
            />
          )}
        {eventResults && data.type === EVENT_TYPES.MATCH && (
          <CompileRpeEvent
            event={data}
            eventResults={eventResults}
            onClose={onClose}
          />
        )}
        {eventResults && data.type === EVENT_TYPES.WORKOUT && (
          <CompileRpeEvent
            event={data}
            eventResults={eventResults}
            onClose={onClose}
          />
        )}
        {eventResults &&
          data.type === EVENT_TYPES.TEST &&
          data.testType === EVENT_TEST_TYPES.DROP_JUMP && (
            <CompileDropJumpEvent
              event={data}
              eventResults={eventResults}
              onClose={onClose}
            />
          )}
        {eventResults &&
          data.type === EVENT_TYPES.SYSTEM_TEST_EVENT && (
            <CompileSystemTestEvent
              event={data}
              eventResults={eventResults}
              onClose={onClose}
            />
          )}
      </>
    );
  }

  return <Fragment />;
};

export const CompileEventModal: FC<{
  isOpen?: IEvent;
  onClose: () => void;
  isLoading?: boolean;
}> = ({ isOpen, isLoading, onClose }) => {
  return (
    <GenericModalV2
      title="Compila Evento"
      size="full"
      isOpen={!!isOpen}
      isLoading={isLoading}
      onClose={onClose}
      classNameBody=""
    >
      {isOpen && <LoadEventProvider event={isOpen} onClose={onClose} />}
    </GenericModalV2>
  );
};
